// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import React, {
  useMemo,
  useRef,
} from 'react';
import {
  Artifact,
  Badge,
  BankAccount,
  Box,
  Card,
  Spinner,
  Text,
  Timeline,
} from '@usealloy/component-library';

import { formatCurrencyIntl } from '~/helpers/i18n/';

import { useEventTypeEvaluationsContext } from '../../context';
import { useTransactionDetail } from '../../hooks';

import { TRANSACTION_STATUS, TRANSACTION_STATUS_DEFAULT, TRANSACTION_STATUS_SUCCESS } from './constants';
import {
  CurrentTransaction,
  CurrentTransactionTimeline,
  CurrentTransactionBankAccount,
  CurrentTransactionExternalId,
} from './styles';

interface CurrentTransactionLabelValueProps {
  label: string;
  value: string;
}

const CurrentTransactionLabelValue: React.FC<CurrentTransactionLabelValueProps> = ({
  label,
  value,
}) => (
  <Box gap={2} direction="column" alignItems="flex-start" style={{ width: 'auto' }}>
    <Text
      size={1}
      lineHeight={3}
      style={{
        color: 'var(--cl-gray-700)',
      }}
    >
      {label}
    </Text>
    <Text size={2} lineHeight={3}>{value}</Text>
  </Box>
);

const AccountsOverview = () => {
  const id = useRef(`currentTransaction_${uuidv4()}`);
  const { evaluation } = useEventTypeEvaluationsContext();
  const { data: transaction, isLoading } = useTransactionDetail({
    entityId: evaluation.entity_id,
    entityToken: evaluation.entity_token,
    evaluationDate: evaluation.timestamp,
    transactionId: evaluation.final_response.supplied.transaction_id,
  });

  const header = useMemo(() => (
    [
      transaction?.process_method ?? evaluation?.final_response?.supplied?.process_method,
      transaction?.category ?? evaluation?.final_response?.supplied?.category,
      'Summary',
    ].filter(Boolean).join(' ')
  ), [evaluation, transaction]);

  const currentTransaction = useMemo(() => {
    if (!transaction) {
      return {};
    }

    const {
      status = null,
      external_transaction_identifier: externalTransactionId,
      amount,
      currency_code: currencyCode,
      description,
      process_method: processMethod,
      category,
      type_code: typeCode,
      interaction_point: interactionPoint,
      source_account_type: sourceAccountType,
      source_bank_account_name: sourceBankAccountName,
      source_bank_account_number: sourceBankAccountNumber,
      source_bank_account_class: sourceBankAccountClass,
      source_bank_account_country_code: sourceBankAccountCountryCode,
      source_bank_account_currency_code: sourceBankAccountCurrencyCode,
      target_account_type: targetAccountType,
      target_bank_account_name: targetBankAccountName,
      target_bank_account_number: targetBankAccountNumber,
      target_bank_account_class: targetBankAccountClass,
      target_bank_account_country_code: targetBankAccountCountryCode,
      target_bank_account_currency_code: targetBankAccountCurrencyCode,
    } = transaction;
    const createdAt = moment(transaction.created_at).format('MMM D, YYYY hh:mm A');
    const {
      backgroundColor,
      badgeStatus,
      dotColorStart,
      dotColorEnd,
      connectorColor,
      connectorStyle,
      icon,
    } = status === null ? TRANSACTION_STATUS_DEFAULT : (
      TRANSACTION_STATUS[status] ?? TRANSACTION_STATUS_SUCCESS
    );
    const { formattedNumber: amountFormatted } = amount
      ? formatCurrencyIntl(
        amount, { alphabeticCurrencyCode: currencyCode },
      )
      : { formattedNumber: '' };

    return {
      createdAt,
      status,
      backgroundColor,
      dotColorStart,
      dotColorEnd,
      connectorColor,
      connectorStyle,
      icon,
      badgeStatus,
      externalTransactionId,
      amount,
      amountFormatted,
      currencyCode,
      description,
      processMethod,
      category,
      typeCode,
      interactionPoint,
      sourceAccountType: capitalize(sourceAccountType),
      sourceBankAccountName,
      sourceBankAccountNumber,
      sourceBankAccountClass,
      sourceBankAccountCountryCode: sourceBankAccountCountryCode ?? 'N/A',
      sourceBankAccountCurrencyCode: sourceBankAccountCurrencyCode ?? 'N/A',
      targetAccountType: capitalize(targetAccountType),
      targetBankAccountName,
      targetBankAccountNumber,
      targetBankAccountClass: targetBankAccountClass ?? sourceBankAccountClass,
      targetBankAccountCountryCode: targetBankAccountCountryCode ?? 'N/A',
      targetBankAccountCurrencyCode: targetBankAccountCurrencyCode ?? 'N/A',
    };
  }, [transaction]);

  const accountSource = {
    name: currentTransaction.sourceBankAccountName,
    accountNumber: currentTransaction.sourceBankAccountNumber,
    accountType: currentTransaction.sourceAccountType,
    class: currentTransaction.sourceBankAccountClass,
    countryCode: currentTransaction.sourceBankAccountCountryCode,
    currencyCode: currentTransaction.sourceBankAccountCurrencyCode,
  };

  const accountTarget = {
    name: currentTransaction.targetBankAccountName,
    accountNumber: currentTransaction.targetBankAccountNumber,
    accountType: currentTransaction.targetAccountType,
    class: currentTransaction.targetBankAccountClass,
    countryCode: currentTransaction.targetBankAccountCountryCode,
    currencyCode: currentTransaction.targetBankAccountCurrencyCode,
  };

  if (isLoading) {
    return (
      <Card
        header={header}
        collapsible
      >
        <Spinner />
      </Card>
    );
  }

  return (
    <Card
      header={header}
      collapsible
    >
      <Box
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={4}
      >
        <Box
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <CurrentTransaction
            color={currentTransaction.backgroundColor}
          >
            <Artifact
              id={id.current}
              header={(
                <Box direction="row" alignItems="center" justifyContent="space-between">
                  <Box direction="column" alignItems="flex-start" gap={2}>
                    <Text
                      style={{
                        color: 'var(--cl-gray-700)',
                      }}
                    >
                      {currentTransaction.createdAt}
                    </Text>
                    <CurrentTransactionExternalId
                      displayContent={currentTransaction.externalTransactionId}
                      copyOnClick
                    />
                  </Box>
                  <Badge
                    status={currentTransaction.badgeStatus}
                    icon={currentTransaction.icon}
                    iconSize="small"
                  >
                    {currentTransaction.status}
                  </Badge>
                </Box>
              )}
            >
              <Box direction="column" gap={2}>
                <Box direction="row" gap={2} alignItems="center" justifyContent="flex-start">
                  <Text as="h3" size={4} lineHeight={4} weight={500}>
                    {currentTransaction.amountFormatted}
                  </Text>
                  <Text style={{ color: 'var(--cl-gray-700)' }}>{currentTransaction.currencyCode}</Text>
                </Box>
                {currentTransaction.description && (
                  <Box direction="row" justifyContent="flex-start">
                    <Text size={2} lineHeight={3}>{currentTransaction.description}</Text>
                  </Box>
                )}
                <Box gap={5} direction="row" justifyContent="flex-start">
                  <CurrentTransactionLabelValue
                    label="Process Method"
                    value={currentTransaction.processMethod}
                  />
                  <CurrentTransactionLabelValue
                    label="Category"
                    value={currentTransaction.category}
                  />
                  <CurrentTransactionLabelValue
                    label="Type Code"
                    value={currentTransaction.typeCode}
                  />
                  <CurrentTransactionLabelValue
                    label="Interaction Point"
                    value={currentTransaction.interactionPoint}
                  />
                </Box>
              </Box>
            </Artifact>
          </CurrentTransaction>
        </Box>
        <CurrentTransactionTimeline>
          <Timeline
            items={[
              {
                content: (
                  <Box gap={3} direction="column" alignItems="flex-start">
                    <Text
                      size={1}
                      weight={700}
                      style={{
                        color: 'var(--cl-gray-700)',
                      }}
                    >
                      From
                    </Text>
                    <CurrentTransactionBankAccount>
                      <BankAccount
                        status="open"
                        size="large"
                        label={currentTransaction.amount <= 0 ? 'Source' : 'Counterparty'}
                        isExternalAccount={currentTransaction.amount > 0}
                        {...currentTransaction.amount <= 0 ? accountSource : accountTarget}
                      />
                    </CurrentTransactionBankAccount>
                  </Box>
                ),
                dot: {
                  color: currentTransaction.dotColorStart,
                  offset: 8,
                  tooltipProps: {
                    content: (
                      <Box
                        direction="column"
                        alignItems="center"
                      >
                        <Text
                          size={1}
                          style={{
                            color: 'var(--cl-gray-000)',
                          }}
                        >
                          The transaction was {currentTransaction.status?.toLowerCase()}
                        </Text>
                        <Text
                          size={1}
                          style={{
                            color: 'var(--cl-gray-000)',
                          }}
                        >
                          at {currentTransaction.createdAt}
                        </Text>
                      </Box>
                    ),
                    side: 'left',
                  },
                },
                connector: {
                  after: {
                    color: currentTransaction.connectorColor,
                    style: currentTransaction.connectorStyle,
                  },
                },
              },
              {
                content: (
                  <Box gap={3} direction="column" alignItems="flex-start">
                    <Text
                      size={1}
                      weight={700}
                      style={{
                        color: 'var(--cl-gray-700)',
                      }}
                    >
                      To
                    </Text>
                    <CurrentTransactionBankAccount>
                      <BankAccount
                        status="open"
                        size="large"
                        label={currentTransaction.amount > 0 ? 'Source' : 'Counterparty'}
                        isExternalAccount={currentTransaction.amount <= 0}
                        {...currentTransaction.amount > 0 ? accountSource : accountTarget}
                      />
                    </CurrentTransactionBankAccount>
                  </Box>
                ),
                dot: {
                  color: currentTransaction.dotColorEnd,
                  offset: 8,
                },
                connector: {
                  before: {
                    color: currentTransaction.connectorColor,
                    style: currentTransaction.connectorStyle,
                    endStyle: 'arrow',
                  },
                },
              },
            ]}
          />
        </CurrentTransactionTimeline>
      </Box>
    </Card>
  );
};

export default AccountsOverview;
